import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, Shield, Star, Compass } from "lucide-react";
import { useSeo } from "../lib/seo";

const values = [
  { icon: Heart, name: "Compassion", desc: "We lead with kindness — every interaction matters, every story is heard." },
  { icon: Shield, name: "Respect", desc: "Your choices, culture, identity and history are honoured without exception." },
  { icon: Users, name: "Inclusion", desc: "Real belonging in real communities — not just attendance, but participation." },
  { icon: Star, name: "Empowerment", desc: "We help build skill and confidence, not dependence on us." },
  { icon: Compass, name: "Independence", desc: "Supports are designed to grow your autonomy, day by day, year by year." },
];

const team = [
  { role: "Founder & Director", initials: "AM", blurb: "Disability advocate with 15+ years in community services." },
  { role: "Clinical Lead", initials: "PR", blurb: "Registered nurse, behaviour support practitioner." },
  { role: "Intake Coordinator", initials: "JS", blurb: "First friendly voice you'll meet when you enquire." },
  { role: "Lead Support Worker", initials: "TN", blurb: "Mentor and trainer for our frontline team." },
];

export default function About() {
  useSeo({
    title: "About Valora | Person-Centred NDIS Provider Servicing Australia",
    description: "Learn about Valora Support Services — our story, our mission and the values that shape how we deliver NDIS Supported Independent Living and core supports across Australia.",
  });

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="bg-gradient-to-b from-[#E6FFFA] via-[#F8FBFA] to-white">
        <div className="container-x py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow">About Valora</p>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight text-[#0F2A2C]">
              A trusted NDIS provider that treats <em className="text-[#0B6E70] not-italic font-semibold">your goals</em> like our own.
            </h1>
            <p className="mt-6 text-lg text-[#4A5568] max-w-xl">
              We started Valora because we kept hearing the same thing from participants and families: "We just want support workers who actually show up, who actually listen." So we built a service around exactly that.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-[28px] overflow-hidden aspect-[5/6] shadow-[0_30px_80px_-30px_rgba(11,110,112,0.4)]">
              <img src="https://customer-assets.emergentagent.com/job_valora-respite/artifacts/97xa8l42_image.png" alt="Support worker vacuuming the living room while a young woman with a leg cast rests on the couch reading" className="h-full w-full object-cover" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="section-y bg-white" data-testid="about-story">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow">Our story</p>
            <h2 className="mt-3 font-serif text-3xl lg:text-4xl font-medium text-[#0F2A2C] tracking-tight">
              Built by people who've been on both sides of support.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-[#4A5568] text-lg leading-relaxed">
            <p>
              Valora was founded by carers, support workers and family members who knew the disability sector could feel warmer, simpler and more honest. We saw participants being shuffled between rotating workers, families exhausted by paperwork, and goals quietly slipping off the bottom of plans.
            </p>
            <p>
              We built a different kind of provider — one that hires for character, trains for skill, and matches every participant with a small, consistent team they actually like. Everything else flows from that foundation: fewer cancellations, real progress on goals, and families who finally feel they can breathe.
            </p>
            <p className="text-[#0F2A2C] font-medium">
              We are not the biggest provider. We are deliberately the kind of provider that knows your name, your routine, and the next step on your plan.
            </p>
          </div>
        </div>
      </section>

      {/* MISSION CARD */}
      <section className="bg-[#F8FBFA]">
        <div className="container-x py-16 lg:py-20">
          <div className="rounded-[28px] bg-gradient-to-br from-[#0B6E70] to-[#45A29E] text-white p-10 lg:p-14 relative overflow-hidden">
            <div aria-hidden className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-[#827397]/30 blur-2xl" />
            <p className="eyebrow text-[#7FD1CB]">Our mission</p>
            <h2 className="mt-3 font-serif text-3xl lg:text-5xl font-medium tracking-tight max-w-4xl">
              "To walk beside every participant with care, respect, and understanding — empowering them to achieve their goals, embrace new opportunities, and live the life they choose."
            </h2>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="section-y bg-white" data-testid="about-values">
        <div className="container-x">
          <div className="max-w-2xl">
            <p className="eyebrow">Our values</p>
            <h2 className="mt-3 font-serif text-3xl lg:text-5xl font-medium text-[#0F2A2C] tracking-tight">Five values. One promise.</h2>
            <p className="mt-5 text-[#4A5568] text-lg">These shape who we hire, how we train and how you'll experience us — every shift, every conversation.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {values.map((v, i) => (
              <div key={v.name} className="card-soft p-6" data-testid={`about-value-${i}`}>
                <div className="h-11 w-11 rounded-xl bg-[#E6FFFA] text-[#0B6E70] flex items-center justify-center mb-4">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#0F2A2C]">{v.name}</h3>
                <p className="mt-2 text-sm text-[#4A5568] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM section removed */}

      {/* CTA */}
      <section className="bg-white">
        <div className="container-x py-16 lg:py-20 text-center">
          <h3 className="font-serif text-3xl lg:text-4xl font-medium text-[#0F2A2C] tracking-tight max-w-3xl mx-auto">Ready to meet the team and see if we're a good fit?</h3>
          <Link to="/referrals" data-testid="about-cta" className="mt-7 btn-primary inline-flex">Start an enquiry <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </div>
  );
}
